import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import { menuInfo } from "@shared/type/CommonType";
import SnSheet from "@shared/components/Grid/SnSheet";

interface Props {
  Menuprops: menuInfo;
  title: string;
  columns: any[];
  rows: any[];
  onApply: (rows: any[]) => Promise<boolean> | boolean;
}

const SN_PRD_WRK_REG_002_TEX_EXCEL_POP = forwardRef(
  ({ Menuprops, title, columns, rows, onApply }: Props, ref) => {
    const gridRef = useRef<any>(null);
    const [rowData] = useState<any[]>(rows ?? []);

    const sheetColumns = useMemo(
      () =>
        (columns ?? []).map((col) => {
          const cdColumn = String(col.cd_column ?? "");
          const next = { ...col };
          if (/^qt_/i.test(cdColumn)) {
            next.yn_sum = next.yn_sum ?? "Y";
            if (!next.tp_column) {
              next.tp_column = "SnNumberBox";
            }
          }
          if (cdColumn.startsWith("dt_")) {
            next.tp_format = next.tp_format ?? "yyyy/MM/dd";
          }
          return next;
        }),
      [columns]
    );

    const ROW_TYPE = {
      ORDER: "수주",
      WORK: "실적",
      RATE: "비율",
    };

    const sizeSlotColumns = useMemo(
      () =>
        (columns ?? [])
          .map((col) => String(col.cd_column ?? ""))
          .filter((cd) => /^qt_work\d+$/i.test(cd)),
      [columns]
    );

    const getSetKey = useCallback((row: any) => {
      if (!row) return "";
      return (
        row.work_set_id ||
        row.no_work ||
        [
          row.no_wo,
          row.no_line_wo,
          row.no_style,
          row.cd_item,
          row.cd_var1,
          row.cd_var2,
        ]
          .filter(Boolean)
          .join("|")
      );
    }, []);

    const recalcRateRow = useCallback(
      (targetRow: any) => {
        if (!targetRow || targetRow.row_type !== ROW_TYPE.WORK) return;

        const setKey = getSetKey(targetRow);
        if (!setKey) return;

        let orderNode: any = null;
        let workNode: any = null;
        let rateNode: any = null;

        gridRef.current?.api?.forEachNode((node: any) => {
          const key = getSetKey(node.data);
          if (key !== setKey) return;

          if (node.data?.row_type === ROW_TYPE.ORDER) orderNode = node;
          if (node.data?.row_type === ROW_TYPE.WORK) workNode = node;
          if (node.data?.row_type === ROW_TYPE.RATE) rateNode = node;
        });

        if (!orderNode || !workNode || !rateNode) return;

        sizeSlotColumns.forEach((slot) => {
          const orderValue = Number(orderNode.data?.[slot] || 0);
          const workValue = Number(workNode.data?.[slot] || 0);
          const rateValue =
            orderValue > 0 ? Math.round((workValue * 100) / orderValue) : 0;

          rateNode.setDataValue(slot, rateValue);
        });
      },
      [ROW_TYPE.WORK, ROW_TYPE.ORDER, ROW_TYPE.RATE, getSetKey, sizeSlotColumns]
    );

    const getExcelColumnLetter = (index: number) => {
      let col = "";
      let n = index + 1;
      while (n > 0) {
        const rem = (n - 1) % 26;
        col = String.fromCharCode(65 + rem) + col;
        n = Math.floor((n - 1) / 26);
      }
      return col;
    };

    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        const editedRows: any[] = [];
        gridRef.current?.api?.forEachNode((node: any) => {
          editedRows.push(node.data);
        });
        const ok = await onApply(editedRows);
        return { excelUpload: !!ok };
      },
    }));

    return (
      <div className="h-full flex flex-col">
        <SnSheet
          menuInfo={Menuprops}
          ref={gridRef}
          columnConfig={sheetColumns}
          rowData={rowData}
          isEditable={true}
          enableAddRow={false}
          title={title}
          codeDataList={[]}
          compareFields={[]}
          onCellValueChanged={(event: any) => {
            recalcRateRow(event?.data);
          }}
          getContextMenuItems={(params: any) => {
            const today = new Date();
            const formattedDate = today
              .toISOString()
              .slice(0, 10)
              .replace(/-/g, "");
            const formattedTime = today
              .toTimeString()
              .slice(0, 8)
              .replace(/:/g, "");

            return [
              {
                name: "내보내기",
                subMenu: [
                  {
                    name: "CSV 내보내기",
                    action: () =>
                      gridRef.current?.api?.exportDataAsExcel({
                        fileName: `${Menuprops?.nm_menu}_Upload_${formattedDate}${formattedTime}.csv`,
                        sheetName: "엑셀업로드",
                      }),
                  },
                  {
                    name: "Excel 내보내기",
                    action: () =>
                      gridRef.current?.api?.exportDataAsExcel({
                        fileName: `${Menuprops?.nm_menu}_Upload_${formattedDate}${formattedTime}.xlsx`,
                        sheetName: "엑셀업로드",
                        autoConvertFormulas: true,
                        processCellCallback: (params: any) => {
                          const rowType = String(
                            params.node?.data?.row_type ?? ""
                          ).trim();
                          const colId = params.column?.getColId?.() ?? "";
                          if (
                            rowType === ROW_TYPE.RATE &&
                            sizeSlotColumns.includes(colId)
                          ) {
                            const displayed =
                              gridRef.current?.api?.getAllDisplayedColumns?.() ||
                              [];
                            const colIndex = displayed.findIndex(
                              (col: any) => col.getColId?.() === colId
                            );
                            const rowIndex = params.node?.rowIndex ?? -1;
                            if (colIndex >= 0 && rowIndex >= 2) {
                              const excelRow = rowIndex + 2;
                              const orderRow = excelRow - 2;
                              const workRow = excelRow - 1;
                              const colLetter = getExcelColumnLetter(colIndex);
                              return `=IF(OR(${colLetter}${orderRow}=0,${colLetter}${workRow}=0),0,ROUND((${colLetter}${workRow}-${colLetter}${orderRow})/${colLetter}${orderRow}*100,0))`;
                            }
                          }
                          return params.value;
                        },
                      }),
                  },
                ],
              },
            ];
          }}
        />
      </div>
    );
  }
);

export default SN_PRD_WRK_REG_002_TEX_EXCEL_POP;
